// WPS-025 v4 · Canvas Card Editor · Executive Light + Bug fixes
// FIXES v4 (Carlos 2026-05-14 19:40):
//   - Field component moved OUTSIDE Inspector → textarea keeps focus while typing
//   - Pointer events bound directly to LAYER (not window) → drag releases cleanly
//   - 4 corner + 4 edge resize handles → visual H/W manipulation
//   - releasePointerCapture explicit on pointerup
//   - Min size 8pt to prevent zero-size collapse
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  cardsApi, templatesApi, assetsApi, fontPairingsApi, wunliAgentApi,
} from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import {
  Type, Image as ImageIcon, Square, Sparkles, Save, Undo2, Redo2,
  Trash2, MoveUp, MoveDown, X, Layers as LayersIcon, FileText, Library,
  Upload, Wand2, ZoomIn, ZoomOut, Maximize2, Grid, RotateCw,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';

const CARD_W_PT = 3.5 * 72;
const CARD_H_PT = 2.25 * 72;
const BLEED_PT  = 9;
const SAFE_PT   = 9;
const GRID_PT   = 6;
const MIN_SIZE  = 8;        // pt
const ZOOM_LEVELS = [0.5, 0.75, 1, 1.5, 2, 3];

// ─── Module-scope components (CRITICAL: never define inside Inspector) ───
const LucideBySlug = ({ slug, size = 28, color = '#01579b' }) => {
  if (!slug) return null;
  const name = String(slug).replace(/^lucide:/, '').replace(/^icon-/, '');
  const pascal = name.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('');
  const Cmp = LucideIcons[pascal];
  if (Cmp) return <Cmp size={size} color={color} strokeWidth={1.5} />;
  return <span style={{ fontSize: 13, color, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>{pascal.slice(0, 3)}</span>;
};

// FIX-1: Field MUST live at module scope so its type identity is stable across renders
const InspectorField = ({ label, children }) => (
  <div style={{ marginBottom: '12px' }}>
    <div style={{ fontSize: '12px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b', marginBottom: '6px', fontWeight: 600 }}>{label}</div>
    {children}
  </div>
);

const INSPECTOR_INPUT_STYLE = {
  width: '100%', padding: '10px 12px', fontSize: '14px',
  border: '1px solid #e2e8f0', background: '#ffffff', color: '#0f172a',
};

const newLayer = (type, overrides = {}) => {
  const base = {
    id: `layer_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    type,
    x: 36, y: 36,
    w: type === 'text' ? 160 : 80,
    h: type === 'text' ? 24 : 80,
    rotation: 0, z: 1,
  };
  if (type === 'text')  return { ...base, content: 'Your text', fontSize: 14, fontWeight: 600, color: '#0f172a', fontFamily: 'Inter', ...overrides };
  if (type === 'icon')  return { ...base, content: '★', fontSize: 32, color: '#01579b', ...overrides };
  if (type === 'image') return { ...base, src: '', ...overrides };
  if (type === 'shape') return { ...base, fill: '#0f172a', shape: 'rect', ...overrides };
  return { ...base, ...overrides };
};

const snap = (v, on) => on ? Math.round(v / GRID_PT) * GRID_PT : v;

// FIX-2 + FIX-3: pointer-driven move/resize logic with proper math per handle
const applyDrag = (layer, drag, dx, dy, snapGrid) => {
  const mode = drag.mode;
  let { origX: x, origY: y, origW: w, origH: h } = drag;
  if (mode === 'move') {
    x = Math.max(0, Math.min(CARD_W_PT - MIN_SIZE, x + dx));
    y = Math.max(0, Math.min(CARD_H_PT - MIN_SIZE, y + dy));
  } else {
    if (mode.includes('w')) { x = drag.origX + dx; w = drag.origW - dx; }
    if (mode.includes('e')) { w = drag.origW + dx; }
    if (mode.includes('n')) { y = drag.origY + dy; h = drag.origH - dy; }
    if (mode.includes('s')) { h = drag.origH + dy; }
    if (w < MIN_SIZE) { if (mode.includes('w')) x = drag.origX + drag.origW - MIN_SIZE; w = MIN_SIZE; }
    if (h < MIN_SIZE) { if (mode.includes('n')) y = drag.origY + drag.origH - MIN_SIZE; h = MIN_SIZE; }
  }
  return {
    ...layer,
    x: snap(x, snapGrid),
    y: snap(y, snapGrid),
    w: snap(w, snapGrid),
    h: snap(h, snapGrid),
  };
};

const CanvasCardEditor = ({ cardId, initialLayout, onSaved }) => {
  const { toast } = useToast();
  const [layers, setLayers] = useState(initialLayout?.layers || []);
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('layers');
  const [zoom, setZoom] = useState(2);
  const [snapGrid, setSnapGrid] = useState(true);
  const [showGuides, setShowGuides] = useState(true);
  const stageRef = useRef(null);
  const dragRef  = useRef(null);
  const rotateRef = useRef(null);
  const fileInputRef = useRef(null);
  const layersRef = useRef(layers);

  // Keep ref in sync for commit-on-pointerup
  useEffect(() => { layersRef.current = layers; }, [layers]);

  const selected = layers.find(l => l.id === selectedId) || null;
  const VIEW_W = CARD_W_PT * zoom;
  const VIEW_H = CARD_H_PT * zoom;

  const pushHistory = useCallback((next) => {
    setHistory(h => [...h.slice(-30), layers]);
    setRedoStack([]);
    setLayers(next);
  }, [layers]);

  const undo = () => {
    if (!history.length) return;
    setHistory(h => h.slice(0, -1));
    setRedoStack(r => [...r, layers]);
    setLayers(history[history.length - 1]);
  };
  const redo = () => {
    if (!redoStack.length) return;
    setRedoStack(r => r.slice(0, -1));
    setHistory(h => [...h, layers]);
    setLayers(redoStack[redoStack.length - 1]);
  };

  const addLayer = (type, overrides = {}) => {
    const l = newLayer(type, overrides);
    l.z = (layers.length ? Math.max(...layers.map(x => x.z || 1)) : 0) + 1;
    pushHistory([...layers, l]);
    setSelectedId(l.id);
  };

  const updateLayer = (id, patch) => pushHistory(layers.map(l => l.id === id ? { ...l, ...patch } : l));
  const deleteLayer = (id) => { pushHistory(layers.filter(l => l.id !== id)); setSelectedId(null); };

  const moveZ = (id, dir) => {
    const arr = [...layers].sort((a, b) => (a.z || 1) - (b.z || 1));
    const idx = arr.findIndex(l => l.id === id);
    if (idx < 0) return;
    const swap = dir === 'up' ? idx + 1 : idx - 1;
    if (swap < 0 || swap >= arr.length) return;
    const z1 = arr[idx].z, z2 = arr[swap].z;
    pushHistory(layers.map(l => l.id === arr[idx].id ? { ...l, z: z2 }
                              : l.id === arr[swap].id ? { ...l, z: z1 } : l));
  };

  // ─── FIX-2: pointer events bound directly to LAYER (no window listeners) ───
  const onLayerPointerDown = (e, layer, mode = 'move') => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(layer.id);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    dragRef.current = {
      id: layer.id, mode,
      startX: e.clientX, startY: e.clientY,
      origX: layer.x, origY: layer.y,
      origW: layer.w, origH: layer.h,
    };
  };

  const onLayerPointerMove = (e) => {
    if (!dragRef.current || dragRef.current.id !== e.currentTarget.dataset.lid && !e.currentTarget.dataset.handle) return;
    if (!dragRef.current) return;
    e.preventDefault();
    const d = dragRef.current;
    const dx = (e.clientX - d.startX) / zoom;
    const dy = (e.clientY - d.startY) / zoom;
    setLayers(prev => prev.map(l => l.id === d.id ? applyDrag(l, d, dx, dy, snapGrid) : l));
  };

  const onLayerPointerUp = (e) => {
    if (!dragRef.current) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
    // commit history snapshot using latest layers
    const finalLayers = layersRef.current;
    setHistory(h => [...h.slice(-30), finalLayers.map(l => ({ ...l }))]);
    setRedoStack([]);
    dragRef.current = null;
  };

  // ─── Rotation handle (separate pointer handlers, bound to handle element) ───
  const onRotatePointerDown = (e, layer) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(layer.id);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    const stageRect = stageRef.current.getBoundingClientRect();
    rotateRef.current = {
      id: layer.id,
      cx: stageRect.left + (layer.x + layer.w / 2) * zoom,
      cy: stageRect.top  + (layer.y + layer.h / 2) * zoom,
    };
  };
  const onRotatePointerMove = (e) => {
    if (!rotateRef.current) return;
    const r = rotateRef.current;
    const ang = Math.atan2(e.clientY - r.cy, e.clientX - r.cx) * 180 / Math.PI + 90;
    setLayers(prev => prev.map(l => l.id === r.id ? { ...l, rotation: Math.round(ang) } : l));
  };
  const onRotatePointerUp = (e) => {
    if (!rotateRef.current) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
    setHistory(h => [...h.slice(-30), layersRef.current.map(l => ({ ...l }))]);
    setRedoStack([]);
    rotateRef.current = null;
  };

  const save = async () => {
    setSaving(true);
    const payload = { canvas: { width: CARD_W_PT, height: CARD_H_PT, units: 'pt' }, layers };
    const r = await cardsApi.saveCanvas(cardId, payload);
    setSaving(false);
    if (r?.success) {
      toast({ title: 'Canvas saved', description: `${r.data?.layers ?? layers.length} layers persisted` });
      onSaved && onSaved(payload);
    } else {
      toast({ title: 'Save failed', variant: 'destructive' });
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') { e.preventDefault(); redo(); }
      if (e.key === 'Delete' && selectedId) deleteLayer(selectedId);
      if (e.key === '+' || e.key === '=') setZoom(z => ZOOM_LEVELS[Math.min(ZOOM_LEVELS.length-1, ZOOM_LEVELS.indexOf(z)+1)] ?? z);
      if (e.key === '-') setZoom(z => ZOOM_LEVELS[Math.max(0, ZOOM_LEVELS.indexOf(z)-1)] ?? z);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [history, redoStack, selectedId]);

  const onStageClick = (e) => {
    if (e.target === stageRef.current) setSelectedId(null);
  };

  const applyTemplate = (template) => {
    const seed = [
      newLayer('text', { content: template.name || 'Company Name', fontSize: 22, fontWeight: 800, x: 18, y: 24, w: 200, h: 30 }),
      newLayer('text', { content: 'Your Name · Title', fontSize: 12, fontWeight: 500, color: '#475569', x: 18, y: 60, w: 200, h: 18 }),
      newLayer('text', { content: template.slogan || 'Trusted local service', fontSize: 10, fontWeight: 400, color: '#94a3b8', x: 18, y: 120, w: 200, h: 16 }),
    ];
    pushHistory([...layers, ...seed.map((s, i) => ({ ...s, z: (layers.length || 0) + i + 1 }))]);
    toast({ title: `Template applied · ${template.name}`, description: '3 starter layers added' });
  };

  const handlers = {
    onPointerDown: onLayerPointerDown,
    onPointerMove: onLayerPointerMove,
    onPointerUp: onLayerPointerUp,
    onPointerCancel: onLayerPointerUp,
  };
  const rotateHandlers = {
    onPointerDown: onRotatePointerDown,
    onPointerMove: onRotatePointerMove,
    onPointerUp: onRotatePointerUp,
    onPointerCancel: onRotatePointerUp,
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '90px 300px 1fr 320px',
      gap: '0', alignItems: 'stretch',
      minHeight: '720px', background: '#f8fafc', color: '#0f172a',
    }}>
      <div style={{ background: '#ffffff', borderRight: '1px solid #e2e8f0' }}>
        <TabIcon icon={<LayersIcon size={20} />}  label="Layers"    active={activeTab==='layers'}    onClick={() => setActiveTab('layers')} />
        <TabIcon icon={<FileText size={20} />}    label="Templates" active={activeTab==='templates'} onClick={() => setActiveTab('templates')} />
        <TabIcon icon={<Library size={20} />}     label="Assets"   active={activeTab==='assets'}   onClick={() => setActiveTab('assets')} />
        <TabIcon icon={<Upload size={20} />}      label="Images"   active={activeTab==='images'}    onClick={() => setActiveTab('images')} />
        <TabIcon icon={<Wand2 size={20} />}       label="AI"       active={activeTab==='ai'}        onClick={() => setActiveTab('ai')} />
      </div>

      <div style={{ background: '#ffffff', borderRight: '1px solid #e2e8f0', overflowY: 'auto', maxHeight: '88vh' }}>
        {activeTab === 'layers'    && <LayersPanel layers={layers} addLayer={addLayer} selectedId={selectedId} onSelect={setSelectedId} onDelete={deleteLayer} moveZ={moveZ} />}
        {activeTab === 'templates' && <TemplatesPanel onApply={applyTemplate} />}
        {activeTab === 'assets'    && <AssetsPanel onAdd={(a) => {
          if (a.type === 'icon') addLayer('icon', { content: a.slug, fontSize: 36, color: '#01579b', w: 48, h: 48 });
          else addLayer('image', { src: a.url, w: 90, h: 60 });
        }} />}
        {activeTab === 'images'    && <ImagesPanel cardId={cardId} fileInputRef={fileInputRef} onAdd={(url) => addLayer('image', { src: url, w: 120, h: 80 })} />}
        {activeTab === 'ai'        && <AIPanel cardId={cardId} onSuggest={(arr) => arr.forEach(l => addLayer(l.type, l))} />}
      </div>

      <div style={{ background: '#f8fafc', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'auto', maxHeight: '88vh' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
          <ToolbarBtn onClick={undo} disabled={!history.length} title="Undo (Ctrl+Z)"><Undo2 size={16} /></ToolbarBtn>
          <ToolbarBtn onClick={redo} disabled={!redoStack.length} title="Redo (Ctrl+Y)"><Redo2 size={16} /></ToolbarBtn>
          <div style={{ width: '1px', height: '22px', background: '#cbd5e1', margin: '0 4px' }} />
          <ToolbarBtn onClick={() => setZoom(z => ZOOM_LEVELS[Math.max(0, ZOOM_LEVELS.indexOf(z)-1)] ?? z)} title="Zoom out (-)"><ZoomOut size={16} /></ToolbarBtn>
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '14px', fontWeight: 600, color: '#0f172a', padding: '0 10px', minWidth: '60px', textAlign: 'center' }}>{Math.round(zoom * 100)}%</div>
          <ToolbarBtn onClick={() => setZoom(z => ZOOM_LEVELS[Math.min(ZOOM_LEVELS.length-1, ZOOM_LEVELS.indexOf(z)+1)] ?? z)} title="Zoom in (+)"><ZoomIn size={16} /></ToolbarBtn>
          <ToolbarBtn onClick={() => setZoom(2)} title="Fit (100%)"><Maximize2 size={16} /></ToolbarBtn>
          <div style={{ width: '1px', height: '22px', background: '#cbd5e1', margin: '0 4px' }} />
          <ToolbarBtn onClick={() => setSnapGrid(!snapGrid)} active={snapGrid} title="Snap to grid"><Grid size={16} /></ToolbarBtn>
          <ToolbarBtn onClick={() => setShowGuides(!showGuides)} active={showGuides} title="Show guides">Guides</ToolbarBtn>
          <div style={{ flex: 1 }} />
          <Button onClick={save} disabled={saving} style={{ background: '#01579b', color: '#ffffff', minWidth: '140px', fontSize: '14px', fontWeight: 700, padding: '10px 16px' }}>
            <Save size={16} style={{ marginRight: 8 }} /> {saving ? 'Saving…' : 'Save Layout'}
          </Button>
        </div>

        <div
          ref={stageRef}
          onClick={onStageClick}
          style={{
            position: 'relative',
            width: VIEW_W, height: VIEW_H,
            background: '#ffffff',
            boxShadow: '0 10px 30px rgba(15,23,42,0.12)',
            overflow: 'hidden', cursor: 'default',
          }}
        >
          {showGuides && (
            <>
              <div style={{ position: 'absolute', left: SAFE_PT * zoom, top: SAFE_PT * zoom, width: VIEW_W - SAFE_PT * zoom * 2, height: VIEW_H - SAFE_PT * zoom * 2, border: '1px dashed rgba(1,87,155,0.45)', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', left: BLEED_PT * zoom / 2, top: BLEED_PT * zoom / 2, width: VIEW_W - BLEED_PT * zoom, height: VIEW_H - BLEED_PT * zoom, border: '1px dashed rgba(220,38,38,0.45)', pointerEvents: 'none' }} />
            </>
          )}
          {[...layers].sort((a, b) => (a.z || 1) - (b.z || 1)).map(l => (
            <LayerView
              key={l.id}
              layer={l}
              zoom={zoom}
              selected={selectedId === l.id}
              handlers={handlers}
              rotateHandlers={rotateHandlers}
            />
          ))}
        </div>

        <div style={{ marginTop: '14px', display: 'flex', gap: '14px', fontSize: '12px', fontFamily: 'JetBrains Mono, monospace', color: '#64748b', letterSpacing: '0.05em', flexWrap: 'wrap', justifyContent: 'center' }}>
          <span>3.5" × 2.25" w/ bleed</span><span>·</span>
          <span>{layers.length} layer{layers.length !== 1 ? 's' : ''}</span><span>·</span>
          <span>grid {snapGrid ? 'ON' : 'OFF'} · {GRID_PT}pt</span>
          {selected && <><span>·</span><span style={{ color: '#01579b', fontWeight: 600 }}>{Math.round(selected.x)} / {Math.round(selected.y)} · {Math.round(selected.w)}×{Math.round(selected.h)} pt</span></>}
        </div>
      </div>

      <div style={{ background: '#ffffff', borderLeft: '1px solid #e2e8f0', overflowY: 'auto', maxHeight: '88vh' }}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '12px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.18em', textTransform: 'uppercase', color: '#01579b', fontWeight: 600 }}>Inspector</div>
        </div>
        {!selected ? (
          <div style={{ padding: '28px 18px', fontSize: '14px', color: '#64748b', textAlign: 'center', lineHeight: 1.5 }}>
            Select a layer to edit · or click a tab on the left to add one
          </div>
        ) : (
          <Inspector
            layer={selected}
            onChange={(p) => updateLayer(selected.id, p)}
            onDelete={() => deleteLayer(selected.id)}
            onMoveUp={() => moveZ(selected.id, 'up')}
            onMoveDown={() => moveZ(selected.id, 'down')}
          />
        )}
      </div>
    </div>
  );
};

// ─────────────── Module-scope components ───────────────

const TabIcon = ({ icon, label, active, onClick }) => (
  <button onClick={onClick} title={label} style={{
    width: '100%', padding: '16px 0',
    background: active ? '#01579b' : 'transparent',
    color: active ? '#ffffff' : '#475569',
    border: 'none', borderLeft: active ? '3px solid #c9a961' : '3px solid transparent',
    cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
    transition: 'all 0.15s ease', fontSize: '12px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600,
  }}>{icon}<span>{label}</span></button>
);

const ToolbarBtn = ({ children, onClick, disabled, active, title }) => (
  <button onClick={onClick} disabled={disabled} title={title} style={{
    background: active ? '#c9a961' : '#f1f5f9',
    color: active ? '#0a0a0a' : '#0f172a',
    border: active ? '1px solid #c9a961' : '1px solid #e2e8f0',
    padding: '8px 12px', cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
    fontSize: '13px', fontWeight: 600, minWidth: '38px',
  }}>{children}</button>
);

const PanelHeader = ({ title }) => (
  <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
    <div style={{ fontSize: '12px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.18em', textTransform: 'uppercase', color: '#01579b', fontWeight: 700 }}>{title}</div>
  </div>
);

const LayersPanel = ({ layers, addLayer, selectedId, onSelect, onDelete, moveZ }) => (
  <>
    <PanelHeader title="Add new layer" />
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', padding: '14px 18px' }}>
      <AddBtn icon={<Type size={20} />}     label="Text"  onClick={() => addLayer('text')} />
      <AddBtn icon={<Sparkles size={20} />}  label="Icon"  onClick={() => addLayer('icon')} />
      <AddBtn icon={<ImageIcon size={20} />} label="Image" onClick={() => addLayer('image')} />
      <AddBtn icon={<Square size={20} />}    label="Shape" onClick={() => addLayer('shape')} />
    </div>
    <PanelHeader title={`Layers · ${layers.length}`} />
    <div style={{ padding: '10px 18px 28px' }}>
      {[...layers].sort((a,b) => (b.z||1) - (a.z||1)).map(l => (
        <div key={l.id} onClick={() => onSelect(l.id)} style={{
          display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', marginBottom: '6px',
          background: selectedId === l.id ? '#e0f2fe' : '#f8fafc',
          border: '1px solid', borderColor: selectedId === l.id ? '#01579b' : '#e2e8f0',
          borderLeft: selectedId === l.id ? '4px solid #01579b' : '1px solid #e2e8f0',
          cursor: 'pointer', fontSize: '14px', color: '#0f172a',
        }}>
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', color: '#01579b', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600 }}>{l.type}</div>
            <div style={{ fontSize: '13px', color: '#475569', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.content || (l.src ? 'image' : l.shape) || '—'}</div>
          </div>
          <button onClick={(e) => { e.stopPropagation(); moveZ(l.id, 'up'); }} style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '4px' }}><MoveUp size={14} /></button>
          <button onClick={(e) => { e.stopPropagation(); moveZ(l.id, 'down'); }} style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '4px' }}><MoveDown size={14} /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(l.id); }} style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px' }}><Trash2 size={14} /></button>
        </div>
      ))}
    </div>
  </>
);

const AddBtn = ({ icon, label, onClick }) => (
  <button onClick={onClick} style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
    padding: '14px 8px', background: '#ffffff', border: '1px solid #e2e8f0',
    cursor: 'pointer', color: '#0f172a', fontSize: '13px', fontWeight: 600,
  }}>{icon}<span>{label}</span></button>
);

const TemplatesPanel = ({ onApply }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const r = await templatesApi.list();
      setTemplates(Array.isArray(r) ? r : (r?.data || []));
      setLoading(false);
    })();
  }, []);
  return (
    <>
      <PanelHeader title={`Templates · ${templates.length}`} />
      <div style={{ padding: '10px 18px 28px' }}>
        {loading && <div style={{ color: '#64748b', fontSize: '13px', padding: '12px 0' }}>Loading…</div>}
        <div style={{ display: 'grid', gap: '8px' }}>
          {templates.map(t => (
            <button key={t.id} onClick={() => onApply(t)} style={{
              padding: '12px 14px', background: '#ffffff', border: '1px solid #e2e8f0',
              cursor: 'pointer', textAlign: 'left', color: '#0f172a',
            }}>
              <div style={{ fontSize: '14px', fontWeight: 700 }}>{t.name || 'Untitled'}</div>
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                {t.category || t.style || 'general'}
              </div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

const AssetsPanel = ({ onAdd }) => {
  const [assets, setAssets] = useState([]);
  const [filter, setFilter] = useState('icon');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      const r = await assetsApi.list({ type: filter });
      setAssets(Array.isArray(r) ? r : (r?.data || []));
      setLoading(false);
    })();
  }, [filter]);
  return (
    <>
      <PanelHeader title="Asset library" />
      <div style={{ display: 'flex', gap: '6px', padding: '10px 18px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
        {['icon', 'photo', 'background'].map(t => (
          <button key={t} onClick={() => setFilter(t)} style={{
            flex: 1, padding: '8px 10px', fontSize: '11px',
            fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.12em', textTransform: 'uppercase',
            background: filter === t ? '#01579b' : '#ffffff',
            color: filter === t ? '#ffffff' : '#475569',
            border: '1px solid', borderColor: filter === t ? '#01579b' : '#e2e8f0',
            cursor: 'pointer', fontWeight: 700,
          }}>{t}</button>
        ))}
      </div>
      <div style={{ padding: '14px 18px 28px' }}>
        {loading && <div style={{ color: '#64748b', fontSize: '13px' }}>Loading…</div>}
        {!loading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {assets.slice(0, 60).map(a => {
              const isLucide = a.rgbPath && a.rgbPath.startsWith('lucide:');
              const slug = isLucide ? a.rgbPath.replace('lucide:', '') : null;
              return (
                <button key={a.id || a.slug} onClick={() => onAdd({
                  type: filter === 'icon' ? 'icon' : 'image',
                  slug: slug || a.slug,
                  url: !isLucide && a.rgbPath ? `https://print.wunli.com${a.rgbPath}` : null,
                  name: a.name,
                })} title={a.name} style={{
                  aspectRatio: '1', background: '#ffffff', border: '1px solid #e2e8f0',
                  cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px',
                  overflow: 'hidden', padding: '8px',
                }}>
                  {isLucide ? <LucideBySlug slug={slug} size={26} color="#01579b" />
                            : a.rgbPath ? <img src={`https://print.wunli.com${a.rgbPath}`} alt={a.name} style={{ width: '100%', height: '70%', objectFit: 'cover' }} />
                                       : <span style={{ fontSize: 20, color: '#01579b' }}>★</span>}
                  <span style={{ fontSize: '10px', color: '#475569', fontWeight: 500, textAlign: 'center', lineHeight: 1.1 }}>{a.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

const ImagesPanel = ({ cardId, onAdd, fileInputRef }) => {
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [recent, setRecent] = useState([]);
  const { toast } = useToast();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const r = await cardsApi.uploadCanvasImage(cardId, f);
    setUploading(false);
    if (r?.success && r.data?.url) {
      const fullUrl = `https://print.wunli.com${r.data.url}`;
      setRecent(prev => [{ url: fullUrl, name: f.name }, ...prev].slice(0, 12));
      onAdd(fullUrl);
      toast({ title: 'Image uploaded', description: f.name });
    } else {
      toast({ title: 'Upload failed', variant: 'destructive' });
    }
  };
  return (
    <>
      <PanelHeader title="Upload image" />
      <div style={{ padding: '14px 18px', display: 'grid', gap: '12px' }}>
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{
          padding: '24px', background: '#f8fafc', border: '2px dashed #cbd5e1',
          cursor: 'pointer', color: '#01579b', fontSize: '14px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', fontWeight: 600,
        }}>
          <Upload size={26} />
          {uploading ? 'Uploading…' : 'Click to upload from device'}
          <span style={{ fontSize: '11px', color: '#64748b', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em', textTransform: 'uppercase' }}>PNG · JPG · SVG · WEBP</span>
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />

        <PanelHeader title="Or paste URL" />
        <div style={{ display: 'flex', gap: '6px' }}>
          <Input value={urlInput} onChange={e => setUrlInput(e.target.value)} placeholder="https://…" style={{ fontSize: '13px' }} />
          <Button onClick={() => { if (urlInput) { onAdd(urlInput); setUrlInput(''); } }} style={{ background: '#01579b', color: '#fff', fontSize: '13px', fontWeight: 600 }}>Add</Button>
        </div>

        {recent.length > 0 && (
          <>
            <PanelHeader title="Recent uploads" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
              {recent.map((r, i) => (
                <button key={i} onClick={() => onAdd(r.url)} style={{
                  aspectRatio: '1', background: `url(${r.url}) center/cover #f1f5f9`,
                  border: '1px solid #e2e8f0', cursor: 'pointer', padding: 0,
                }} title={r.name} />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};

const AIPanel = ({ cardId, onSuggest }) => {
  const [industry, setIndustry] = useState('');
  const [tone, setTone] = useState('modern');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const ask = async () => {
    setLoading(true); setSuggestions([]);
    const r = await wunliAgentApi.ask('canvas_layout', { industry: industry || 'general', tone });
    setLoading(false);
    setSuggestions(r?.suggestions || []);
  };
  const apply = () => {
    const positions = {
      'top-left':     { x: 18,  y: 18 },
      'top-right':    { x: 145, y: 18 },
      'center':       { x: 80,  y: 60 },
      'bottom-left':  { x: 18,  y: 120 },
      'bottom-right': { x: 145, y: 120 },
    };
    const arr = suggestions.map(line => {
      const [type, content, pos] = line.split('|').map(p => p.trim());
      const t = (type || '').toLowerCase().includes('icon') ? 'icon'
              : (type || '').toLowerCase().includes('shape') ? 'shape' : 'text';
      const p = positions[(pos || '').toLowerCase()] || positions['center'];
      if (t === 'text')  return { type: 'text',  content: content || 'Sample', fontSize: 13, fontWeight: 600, color: '#0a0a0a', x: p.x, y: p.y, w: 140, h: 22 };
      if (t === 'icon')  return { type: 'icon',  content: content || '★', fontSize: 28, color: '#01579b', x: p.x, y: p.y, w: 40, h: 40 };
      if (t === 'shape') return { type: 'shape', shape: 'rect', fill: '#0a0a0a', x: p.x, y: p.y, w: 60, h: 4 };
      return null;
    }).filter(Boolean);
    if (arr.length) onSuggest(arr);
  };
  return (
    <>
      <PanelHeader title="Wunli AI · Layout Generator" />
      <div style={{ padding: '14px 18px', display: 'grid', gap: '12px' }}>
        <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>Tell Wunli AI your industry and tone · it will suggest a 4-6 layer starting layout.</div>
        <Input value={industry} onChange={e => setIndustry(e.target.value)} placeholder="industry (e.g. plumbing)" style={{ fontSize: '13px' }} />
        <select value={tone} onChange={e => setTone(e.target.value)} style={{ padding: '10px', background: '#fff', color: '#0f172a', border: '1px solid #e2e8f0', fontSize: '13px' }}>
          <option value="modern">Modern</option><option value="bold">Bold</option><option value="luxury">Luxury</option><option value="minimal">Minimal</option><option value="classic">Classic</option>
        </select>
        <Button onClick={ask} disabled={loading} style={{ background: '#c9a961', color: '#0a0a0a', fontWeight: 700, fontSize: '14px', padding: '12px' }}>
          <Sparkles size={16} style={{ marginRight: 8 }} /> {loading ? 'Asking Wunli AI…' : 'Generate layout'}
        </Button>
        {suggestions.length > 0 && (
          <>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px', fontSize: '12px', fontFamily: 'JetBrains Mono, monospace', color: '#0f172a', maxHeight: '200px', overflowY: 'auto' }}>
              {suggestions.map((s, i) => <div key={i} style={{ marginBottom: '6px' }}>{s}</div>)}
            </div>
            <Button onClick={apply} style={{ background: '#01579b', color: '#fff', fontSize: '13px', fontWeight: 700, padding: '10px' }}>
              Apply suggestions as layers
            </Button>
          </>
        )}
      </div>
    </>
  );
};

// ─── Layer renderer · pointer handlers + 8 resize handles when selected ───
const ResizeHandle = ({ pos, layer, handlers }) => {
  const styles = {
    nw: { left: -5, top: -5, cursor: 'nwse-resize' },
    n:  { left: '50%', top: -5, marginLeft: -5, cursor: 'ns-resize' },
    ne: { right: -5, top: -5, cursor: 'nesw-resize' },
    e:  { right: -5, top: '50%', marginTop: -5, cursor: 'ew-resize' },
    se: { right: -5, bottom: -5, cursor: 'nwse-resize' },
    s:  { left: '50%', bottom: -5, marginLeft: -5, cursor: 'ns-resize' },
    sw: { left: -5, bottom: -5, cursor: 'nesw-resize' },
    w:  { left: -5, top: '50%', marginTop: -5, cursor: 'ew-resize' },
  };
  return (
    <div
      onPointerDown={(e) => handlers.onPointerDown(e, layer, pos)}
      onPointerMove={handlers.onPointerMove}
      onPointerUp={handlers.onPointerUp}
      onPointerCancel={handlers.onPointerCancel}
      style={{
        position: 'absolute', width: 10, height: 10,
        background: '#ffffff', border: '2px solid #01579b',
        ...styles[pos], zIndex: 10,
      }}
    />
  );
};

const LayerView = ({ layer, zoom, selected, handlers, rotateHandlers }) => {
  const isLucideIcon = layer.type === 'icon' && layer.content && /^[a-z-]+$/.test(layer.content) && layer.content.length > 1 && !/^[★●◆■▲]/.test(layer.content);
  const outerStyle = {
    position: 'absolute',
    left:   layer.x * zoom,
    top:    layer.y * zoom,
    width:  layer.w * zoom,
    height: layer.h * zoom,
    transform: `rotate(${layer.rotation || 0}deg)`,
    transformOrigin: 'center center',
    outline: selected ? '2px solid #01579b' : 'none',
    outlineOffset: '1px',
    userSelect: 'none',
    touchAction: 'none',
    cursor: 'move',
  };

  // Inner content depends on type
  const inner = (() => {
    if (layer.type === 'text') return (
      <div style={{
        width: '100%', height: '100%',
        fontFamily: `'${layer.fontFamily || 'Inter'}', sans-serif`,
        fontSize: (layer.fontSize || 14) * zoom,
        fontWeight: layer.fontWeight || 600,
        color: layer.color || '#0f172a',
        display: 'flex', alignItems: 'center',
        overflow: 'hidden', pointerEvents: 'none',
      }}>{layer.content || ''}</div>
    );
    if (layer.type === 'icon') return (
      <div style={{
        width: '100%', height: '100%',
        color: layer.color || '#01579b',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
      }}>
        {isLucideIcon
          ? <LucideBySlug slug={layer.content} size={Math.min(layer.w, layer.h) * zoom * 0.75} color={layer.color || '#01579b'} />
          : <span style={{ fontSize: (layer.fontSize || 32) * zoom }}>{layer.content || '★'}</span>}
      </div>
    );
    if (layer.type === 'image') return (
      <div style={{
        width: '100%', height: '100%',
        background: layer.src ? `url(${layer.src}) center/contain no-repeat #ffffff` : '#f1f5f9',
        border: layer.src ? '1px solid transparent' : '1px dashed #94a3b8',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
      }}>
        {!layer.src && <span style={{ fontSize: '12px', color: '#64748b', fontFamily: 'JetBrains Mono, monospace' }}>IMG URL</span>}
      </div>
    );
    if (layer.type === 'shape') return (
      <div style={{
        width: '100%', height: '100%',
        background: layer.fill || '#0f172a',
        borderRadius: layer.shape === 'circle' ? '50%' : 0,
        pointerEvents: 'none',
      }} />
    );
    return null;
  })();

  return (
    <div
      data-lid={layer.id}
      onPointerDown={(e) => handlers.onPointerDown(e, layer, 'move')}
      onPointerMove={handlers.onPointerMove}
      onPointerUp={handlers.onPointerUp}
      onPointerCancel={handlers.onPointerCancel}
      style={outerStyle}
    >
      {inner}
      {selected && (
        <>
          {/* 4 corner + 4 edge resize handles */}
          <ResizeHandle pos="nw" layer={layer} handlers={handlers} />
          <ResizeHandle pos="n"  layer={layer} handlers={handlers} />
          <ResizeHandle pos="ne" layer={layer} handlers={handlers} />
          <ResizeHandle pos="e"  layer={layer} handlers={handlers} />
          <ResizeHandle pos="se" layer={layer} handlers={handlers} />
          <ResizeHandle pos="s"  layer={layer} handlers={handlers} />
          <ResizeHandle pos="sw" layer={layer} handlers={handlers} />
          <ResizeHandle pos="w"  layer={layer} handlers={handlers} />
          {/* Rotation handle (top-center, above the layer) */}
          <div
            onPointerDown={(e) => rotateHandlers.onPointerDown(e, layer)}
            onPointerMove={rotateHandlers.onPointerMove}
            onPointerUp={rotateHandlers.onPointerUp}
            onPointerCancel={rotateHandlers.onPointerCancel}
            style={{
              position: 'absolute', left: '50%', top: -30, marginLeft: -11,
              width: 22, height: 22, borderRadius: '50%', background: '#c9a961',
              color: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'grab', boxShadow: '0 2px 8px rgba(0,0,0,0.25)', zIndex: 10,
            }}><RotateCw size={12} /></div>
        </>
      )}
    </div>
  );
};

// ─── Inspector · Field is at module scope (FIX-1) ───
const Inspector = ({ layer, onChange, onDelete, onMoveUp, onMoveDown }) => {
  const [fontPairings, setFontPairings] = useState([]);
  useEffect(() => {
    if (layer.type === 'text' && fontPairings.length === 0) {
      (async () => {
        const r = await fontPairingsApi.list();
        setFontPairings(Array.isArray(r) ? r : (r?.data || []));
      })();
    }
    // eslint-disable-next-line
  }, [layer.type]);

  return (
    <div style={{ padding: '16px 18px' }}>
      <div style={{ fontSize: '14px', fontWeight: 800, color: '#01579b', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'JetBrains Mono, monospace' }}>{layer.type}</div>

      {layer.type === 'text' && (
        <>
          <InspectorField label="Text">
            <textarea
              value={layer.content || ''}
              onChange={e => onChange({ content: e.target.value })}
              rows={2}
              style={{ ...INSPECTOR_INPUT_STYLE, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </InspectorField>
          {fontPairings.length > 0 && (
            <InspectorField label="Font (20 curated)">
              <select value={layer.fontFamily || 'Inter'} onChange={e => {
                const fp = fontPairings.find(f => f.display?.family === e.target.value);
                onChange({ fontFamily: e.target.value, fontWeight: fp?.display?.weight || layer.fontWeight });
              }} style={INSPECTOR_INPUT_STYLE}>
                <option value="Inter">Inter (default)</option>
                {fontPairings.map(p => (
                  <option key={p.id || p.slug} value={p.display?.family}>{p.display?.family} · {p.name}</option>
                ))}
              </select>
            </InspectorField>
          )}
          <InspectorField label="Font size (pt)">
            <input type="number" value={layer.fontSize || 14} min={8} max={48} onChange={e => onChange({ fontSize: parseInt(e.target.value) || 14 })} style={INSPECTOR_INPUT_STYLE} />
          </InspectorField>
          <InspectorField label="Weight">
            <select value={layer.fontWeight || 600} onChange={e => onChange({ fontWeight: parseInt(e.target.value) })} style={INSPECTOR_INPUT_STYLE}>
              {[300,400,500,600,700,800,900].map(w => <option key={w} value={w}>{w}</option>)}
            </select>
          </InspectorField>
          <InspectorField label="Color">
            <input type="color" value={layer.color || '#0f172a'} onChange={e => onChange({ color: e.target.value })} style={{ width: '100%', height: '40px', border: '1px solid #e2e8f0', cursor: 'pointer' }} />
          </InspectorField>
        </>
      )}
      {layer.type === 'icon' && (
        <>
          <InspectorField label="Glyph or Lucide slug">
            <input value={layer.content || '★'} onChange={e => onChange({ content: e.target.value })} placeholder="★ or 'wrench'" style={INSPECTOR_INPUT_STYLE} />
          </InspectorField>
          <InspectorField label="Size (pt)">
            <input type="number" value={layer.fontSize || 32} min={12} max={80} onChange={e => onChange({ fontSize: parseInt(e.target.value) || 32 })} style={INSPECTOR_INPUT_STYLE} />
          </InspectorField>
          <InspectorField label="Color">
            <input type="color" value={layer.color || '#01579b'} onChange={e => onChange({ color: e.target.value })} style={{ width: '100%', height: '40px', border: '1px solid #e2e8f0', cursor: 'pointer' }} />
          </InspectorField>
        </>
      )}
      {layer.type === 'image' && (
        <InspectorField label="Image URL">
          <input value={layer.src || ''} onChange={e => onChange({ src: e.target.value })} placeholder="https://…" style={INSPECTOR_INPUT_STYLE} />
          {layer.src && (
            <div style={{ marginTop: '10px', padding: '10px', background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80px' }}>
              <img src={layer.src} alt="preview" style={{ maxWidth: '100%', maxHeight: '120px', objectFit: 'contain' }} />
            </div>
          )}
        </InspectorField>
      )}
      {layer.type === 'shape' && (
        <>
          <InspectorField label="Shape">
            <select value={layer.shape || 'rect'} onChange={e => onChange({ shape: e.target.value })} style={INSPECTOR_INPUT_STYLE}>
              <option value="rect">Rectangle</option><option value="circle">Circle</option>
            </select>
          </InspectorField>
          <InspectorField label="Fill">
            <input type="color" value={layer.fill || '#0f172a'} onChange={e => onChange({ fill: e.target.value })} style={{ width: '100%', height: '40px', border: '1px solid #e2e8f0', cursor: 'pointer' }} />
          </InspectorField>
        </>
      )}

      <div style={{ height: '1px', background: '#e2e8f0', margin: '16px 0' }} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <InspectorField label="X (pt)"><input type="number" value={Math.round(layer.x)} onChange={e => onChange({ x: parseFloat(e.target.value) || 0 })} style={INSPECTOR_INPUT_STYLE} /></InspectorField>
        <InspectorField label="Y (pt)"><input type="number" value={Math.round(layer.y)} onChange={e => onChange({ y: parseFloat(e.target.value) || 0 })} style={INSPECTOR_INPUT_STYLE} /></InspectorField>
        <InspectorField label="W (pt)"><input type="number" value={Math.round(layer.w)} onChange={e => onChange({ w: parseFloat(e.target.value) || 0 })} style={INSPECTOR_INPUT_STYLE} /></InspectorField>
        <InspectorField label="H (pt)"><input type="number" value={Math.round(layer.h)} onChange={e => onChange({ h: parseFloat(e.target.value) || 0 })} style={INSPECTOR_INPUT_STYLE} /></InspectorField>
      </div>
      <InspectorField label="Rotation (°)">
        <input type="range" value={layer.rotation || 0} min={-180} max={180} step={1} onChange={e => onChange({ rotation: parseInt(e.target.value) })} style={{ width: '100%' }} />
        <div style={{ fontSize: '13px', color: '#01579b', fontFamily: 'JetBrains Mono, monospace', textAlign: 'right', fontWeight: 700 }}>{layer.rotation || 0}°</div>
      </InspectorField>

      <div style={{ display: 'flex', gap: '6px', marginTop: '14px' }}>
        <Button size="sm" variant="outline" onClick={onMoveUp} title="Move up"><MoveUp size={14} /></Button>
        <Button size="sm" variant="outline" onClick={onMoveDown} title="Move down"><MoveDown size={14} /></Button>
        <Button size="sm" onClick={onDelete} style={{ background: '#dc2626', color: '#fff', marginLeft: 'auto', fontSize: '13px', fontWeight: 600 }}><Trash2 size={14} /></Button>
      </div>
    </div>
  );
};

export default CanvasCardEditor;
